// Indicator Registry for Silk Road Terminal
// Central registry of all tracked economic indicators with metadata

import type { IndicatorMetadata, IndicatorCategory, DataSource } from '../../types/indicator';

// Complete indicator definitions
export const INDICATORS: IndicatorMetadata[] = [
  // ============================================
  // ECONOMIC OUTPUT (GDP & Growth)
  // ============================================
  {
    id: 'gdp',
    name: 'GDP (Current USD)',
    description: 'Gross Domestic Product at current US dollar prices',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GDP.MKTP.CD',
    category: 'economic_output',
    subcategory: 'GDP',
    goodDirection: 'up',
    tooltip: 'Total market value of all final goods and services produced within a country in a given year',
    isPrimary: true,
  },
  {
    id: 'gdp_growth',
    name: 'GDP Growth Rate',
    description: 'Annual percentage growth rate of GDP at constant prices',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GDP.MKTP.KD.ZG',
    category: 'economic_output',
    subcategory: 'GDP',
    goodDirection: 'up',
    thresholds: { excellent: 7, good: 5, warning: 2, critical: 0 },
    tooltip: 'Measures how fast the economy is expanding or contracting',
    isPrimary: true,
  },
  {
    id: 'gdp_per_capita',
    name: 'GDP per Capita',
    description: 'GDP divided by total population',
    unit: 'USD',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GDP.PCAP.CD',
    category: 'economic_output',
    subcategory: 'GDP',
    goodDirection: 'up',
    tooltip: 'Average economic output per person, a key measure of living standards',
    isPrimary: true,
  },
  {
    id: 'gdp_per_capita_ppp',
    name: 'GDP per Capita (PPP)',
    description: 'GDP per capita adjusted for purchasing power parity',
    unit: 'USD',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GDP.PCAP.PP.CD',
    category: 'economic_output',
    subcategory: 'GDP',
    goodDirection: 'up',
    tooltip: 'GDP per capita adjusted for price level differences between countries',
  },
  {
    id: 'gni',
    name: 'Gross National Income',
    description: 'Total income earned by a nation\'s residents',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GNP.MKTP.CD',
    category: 'economic_output',
    subcategory: 'National Income',
    goodDirection: 'up',
  },
  {
    id: 'gni_per_capita',
    name: 'GNI per Capita',
    description: 'Gross National Income per person',
    unit: 'USD',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GNP.PCAP.CD',
    category: 'economic_output',
    subcategory: 'National Income',
    goodDirection: 'up',
  },

  // ============================================
  // PRICES & INFLATION
  // ============================================
  {
    id: 'inflation_cpi',
    name: 'Inflation Rate (CPI)',
    description: 'Annual percentage change in consumer prices',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'FP.CPI.TOTL.ZG',
    category: 'prices',
    subcategory: 'Consumer Prices',
    goodDirection: 'neutral',
    thresholds: { excellent: 3, good: 5, warning: 10, critical: 20 },
    tooltip: 'Measures the rate at which consumer prices are rising',
    isPrimary: true,
  },
  {
    id: 'inflation_gdp_deflator',
    name: 'GDP Deflator',
    description: 'Ratio of nominal GDP to real GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NY.GDP.DEFL.KD.ZG',
    category: 'prices',
    subcategory: 'Price Indices',
    goodDirection: 'neutral',
  },

  // ============================================
  // EXTERNAL SECTOR
  // ============================================
  {
    id: 'fdi_inflows',
    name: 'FDI Net Inflows',
    description: 'Foreign Direct Investment net inflows in current USD',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'BX.KLT.DINV.CD.WD',
    category: 'external_sector',
    subcategory: 'Foreign Investment',
    goodDirection: 'up',
    tooltip: 'Capital invested in the country by foreign entities',
    isPrimary: true,
  },
  {
    id: 'fdi_gdp',
    name: 'FDI as % of GDP',
    description: 'Foreign Direct Investment as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'BX.KLT.DINV.WD.GD.ZS',
    category: 'external_sector',
    subcategory: 'Foreign Investment',
    goodDirection: 'up',
    thresholds: { excellent: 10, good: 5, warning: 2, critical: 0 },
  },
  {
    id: 'current_account_gdp',
    name: 'Current Account Balance (% GDP)',
    description: 'Sum of net exports, net income, and net transfers as % of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'BN.CAB.XOKA.GD.ZS',
    category: 'external_sector',
    subcategory: 'Balance of Payments',
    goodDirection: 'neutral',
    thresholds: { excellent: 5, good: 0, warning: -5, critical: -10 },
    isPrimary: true,
  },
  {
    id: 'exports_gdp',
    name: 'Exports (% of GDP)',
    description: 'Exports of goods and services as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NE.EXP.GNFS.ZS',
    category: 'external_sector',
    subcategory: 'Trade',
    goodDirection: 'up',
  },
  {
    id: 'imports_gdp',
    name: 'Imports (% of GDP)',
    description: 'Imports of goods and services as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NE.IMP.GNFS.ZS',
    category: 'external_sector',
    subcategory: 'Trade',
    goodDirection: 'neutral',
  },
  {
    id: 'trade_gdp',
    name: 'Trade (% of GDP)',
    description: 'Sum of exports and imports as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'NE.TRD.GNFS.ZS',
    category: 'external_sector',
    subcategory: 'Trade',
    goodDirection: 'up',
    tooltip: 'Measures trade openness of the economy',
  },
  {
    id: 'reserves',
    name: 'Total Reserves (inc. Gold)',
    description: 'Foreign exchange reserves including gold',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'FI.RES.TOTL.CD',
    category: 'external_sector',
    subcategory: 'Reserves',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'reserves_months_import',
    name: 'Reserves (Months of Import)',
    description: 'Total reserves in terms of months of imports',
    unit: 'months',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'FI.RES.TOTL.MO',
    category: 'external_sector',
    subcategory: 'Reserves',
    goodDirection: 'up',
    thresholds: { excellent: 6, good: 4, warning: 3, critical: 2 },
  },
  {
    id: 'remittances',
    name: 'Personal Remittances Received',
    description: 'Personal transfers and compensation of employees received',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'BX.TRF.PWKR.CD.DT',
    category: 'external_sector',
    subcategory: 'Remittances',
    goodDirection: 'up',
  },
  {
    id: 'remittances_gdp',
    name: 'Remittances (% of GDP)',
    description: 'Personal remittances received as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'BX.TRF.PWKR.DT.GD.ZS',
    category: 'external_sector',
    subcategory: 'Remittances',
    goodDirection: 'neutral',
  },
  {
    id: 'external_debt',
    name: 'External Debt Stock',
    description: 'Total external debt outstanding',
    unit: 'USD',
    scale: 'billion',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'DT.DOD.DECT.CD',
    category: 'external_sector',
    subcategory: 'External Debt',
    goodDirection: 'down',
  },
  {
    id: 'external_debt_gni',
    name: 'External Debt (% of GNI)',
    description: 'Total external debt as percentage of GNI',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'DT.DOD.DECT.GN.ZS',
    category: 'external_sector',
    subcategory: 'External Debt',
    goodDirection: 'down',
    thresholds: { excellent: 30, good: 50, warning: 80, critical: 100 },
  },

  // ============================================
  // FISCAL
  // ============================================
  {
    id: 'govt_debt_gdp',
    name: 'Government Debt (% of GDP)',
    description: 'Central government debt as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'GC.DOD.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Public Debt',
    goodDirection: 'down',
    thresholds: { excellent: 30, good: 50, warning: 70, critical: 100 },
    isPrimary: true,
  },
  {
    id: 'govt_revenue_gdp',
    name: 'Government Revenue (% of GDP)',
    description: 'General government revenue as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'GC.REV.XGRT.GD.ZS',
    category: 'fiscal',
    subcategory: 'Government Finance',
    goodDirection: 'up',
  },
  {
    id: 'govt_expenditure_gdp',
    name: 'Government Expenditure (% of GDP)',
    description: 'General government total expenditure as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'GC.XPN.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Government Finance',
    goodDirection: 'neutral',
  },
  {
    id: 'tax_revenue_gdp',
    name: 'Tax Revenue (% of GDP)',
    description: 'Tax revenue as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'GC.TAX.TOTL.GD.ZS',
    category: 'fiscal',
    subcategory: 'Government Finance',
    goodDirection: 'neutral',
  },

  // ============================================
  // LABOR MARKET
  // ============================================
  {
    id: 'unemployment',
    name: 'Unemployment Rate',
    description: 'Percentage of labor force that is unemployed',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SL.UEM.TOTL.ZS',
    category: 'labor',
    subcategory: 'Employment',
    goodDirection: 'down',
    thresholds: { excellent: 3, good: 5, warning: 8, critical: 12 },
    isPrimary: true,
  },
  {
    id: 'unemployment_youth',
    name: 'Youth Unemployment Rate',
    description: 'Percentage of youth labor force (15-24) that is unemployed',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SL.UEM.1524.ZS',
    category: 'labor',
    subcategory: 'Employment',
    goodDirection: 'down',
  },
  {
    id: 'labor_force',
    name: 'Labor Force Total',
    description: 'Total labor force (economically active population)',
    unit: 'people',
    scale: 'million',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SL.TLF.TOTL.IN',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },
  {
    id: 'labor_participation',
    name: 'Labor Force Participation Rate',
    description: 'Proportion of population ages 15+ that is economically active',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SL.TLF.CACT.ZS',
    category: 'labor',
    subcategory: 'Labor Force',
    goodDirection: 'up',
  },

  // ============================================
  // DEMOGRAPHICS
  // ============================================
  {
    id: 'population',
    name: 'Total Population',
    description: 'Total population count',
    unit: 'people',
    scale: 'million',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SP.POP.TOTL',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'neutral',
    isPrimary: true,
  },
  {
    id: 'population_growth',
    name: 'Population Growth Rate',
    description: 'Annual population growth rate',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SP.POP.GROW',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'neutral',
  },
  {
    id: 'population_density',
    name: 'Population Density',
    description: 'People per square kilometer of land area',
    unit: 'per km²',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'EN.POP.DNST',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'neutral',
  },
  {
    id: 'urban_population',
    name: 'Urban Population (%)',
    description: 'People living in urban areas as percentage of total',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SP.URB.TOTL.IN.ZS',
    category: 'demographics',
    subcategory: 'Urbanization',
    goodDirection: 'neutral',
  },
  {
    id: 'life_expectancy',
    name: 'Life Expectancy at Birth',
    description: 'Average number of years a newborn is expected to live',
    unit: 'years',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SP.DYN.LE00.IN',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'fertility_rate',
    name: 'Fertility Rate',
    description: 'Average number of children per woman',
    unit: 'births',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SP.DYN.TFRT.IN',
    category: 'demographics',
    subcategory: 'Population',
    goodDirection: 'neutral',
  },

  // ============================================
  // BUSINESS ENVIRONMENT
  // ============================================
  {
    id: 'ease_of_business',
    name: 'Ease of Doing Business Score',
    description: 'Overall ease of doing business score (0-100)',
    unit: 'score',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IC.BUS.EASE.XQ',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'up',
    thresholds: { excellent: 80, good: 70, warning: 50, critical: 30 },
    isPrimary: true,
  },
  {
    id: 'time_start_business',
    name: 'Time to Start Business',
    description: 'Days required to start a business',
    unit: 'days',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IC.REG.DURS',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'down',
  },
  {
    id: 'cost_start_business',
    name: 'Cost to Start Business',
    description: 'Cost to start a business (% of GNI per capita)',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IC.REG.COST.PC.ZS',
    category: 'business',
    subcategory: 'Business Climate',
    goodDirection: 'down',
  },

  // ============================================
  // DIGITAL & TECHNOLOGY
  // ============================================
  {
    id: 'internet_users',
    name: 'Internet Users (%)',
    description: 'Individuals using the Internet (% of population)',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IT.NET.USER.ZS',
    category: 'digital',
    subcategory: 'Connectivity',
    goodDirection: 'up',
    isPrimary: true,
  },
  {
    id: 'mobile_subscriptions',
    name: 'Mobile Cellular Subscriptions',
    description: 'Mobile cellular subscriptions per 100 people',
    unit: 'per 100',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IT.CEL.SETS.P2',
    category: 'digital',
    subcategory: 'Connectivity',
    goodDirection: 'up',
  },
  {
    id: 'fixed_broadband',
    name: 'Fixed Broadband Subscriptions',
    description: 'Fixed broadband subscriptions per 100 people',
    unit: 'per 100',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'IT.NET.BBND.P2',
    category: 'digital',
    subcategory: 'Connectivity',
    goodDirection: 'up',
  },

  // ============================================
  // EDUCATION
  // ============================================
  {
    id: 'school_enrollment_primary',
    name: 'School Enrollment, Primary',
    description: 'Gross enrollment ratio, primary education',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SE.PRM.ENRR',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'school_enrollment_secondary',
    name: 'School Enrollment, Secondary',
    description: 'Gross enrollment ratio, secondary education',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SE.SEC.ENRR',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'school_enrollment_tertiary',
    name: 'School Enrollment, Tertiary',
    description: 'Gross enrollment ratio, tertiary education',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SE.TER.ENRR',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },
  {
    id: 'literacy_rate',
    name: 'Literacy Rate',
    description: 'Adult literacy rate (% of people ages 15+)',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SE.ADT.LITR.ZS',
    category: 'demographics',
    subcategory: 'Education',
    goodDirection: 'up',
  },

  // ============================================
  // HEALTH
  // ============================================
  {
    id: 'health_expenditure_gdp',
    name: 'Health Expenditure (% of GDP)',
    description: 'Current health expenditure as percentage of GDP',
    unit: '%',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SH.XPD.CHEX.GD.ZS',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'up',
  },
  {
    id: 'hospital_beds',
    name: 'Hospital Beds',
    description: 'Hospital beds per 1,000 people',
    unit: 'per 1000',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SH.MED.BEDS.ZS',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'up',
  },
  {
    id: 'physicians',
    name: 'Physicians',
    description: 'Physicians per 1,000 people',
    unit: 'per 1000',
    frequency: 'annual',
    source: 'worldbank',
    sourceCode: 'SH.MED.PHYS.ZS',
    category: 'demographics',
    subcategory: 'Health',
    goodDirection: 'up',
  },
];

// Category definitions
export const CATEGORIES: Array<{
  id: IndicatorCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
}> = [
  {
    id: 'economic_output',
    name: 'Economic Output',
    description: 'GDP, growth, and national income',
    icon: 'TrendingUp',
    color: '#22c55e',
  },
  {
    id: 'prices',
    name: 'Prices & Inflation',
    description: 'Consumer prices and inflation measures',
    icon: 'DollarSign',
    color: '#f59e0b',
  },
  {
    id: 'external_sector',
    name: 'External Sector',
    description: 'Trade, FDI, reserves, and external debt',
    icon: 'Globe',
    color: '#3b82f6',
  },
  {
    id: 'fiscal',
    name: 'Fiscal',
    description: 'Government finances and public debt',
    icon: 'Landmark',
    color: '#8b5cf6',
  },
  {
    id: 'monetary',
    name: 'Monetary',
    description: 'Interest rates and money supply',
    icon: 'Building',
    color: '#06b6d4',
  },
  {
    id: 'labor',
    name: 'Labor Market',
    description: 'Employment and labor force',
    icon: 'Briefcase',
    color: '#ec4899',
  },
  {
    id: 'demographics',
    name: 'Demographics',
    description: 'Population, health, and education',
    icon: 'Users',
    color: '#f97316',
  },
  {
    id: 'business',
    name: 'Business Environment',
    description: 'Ease of doing business metrics',
    icon: 'Building',
    color: '#14b8a6',
  },
  {
    id: 'digital',
    name: 'Digital & Technology',
    description: 'Internet, mobile, and digital connectivity',
    icon: 'Wifi',
    color: '#6366f1',
  },
  {
    id: 'ratings',
    name: 'Ratings & Risk',
    description: 'Sovereign ratings and risk scores',
    icon: 'Shield',
    color: '#ef4444',
  },
];

// Helper functions
export const getIndicatorById = (id: string): IndicatorMetadata | undefined => {
  return INDICATORS.find(ind => ind.id === id);
};

export const getIndicatorsByCategory = (category: IndicatorCategory): IndicatorMetadata[] => {
  return INDICATORS.filter(ind => ind.category === category);
};

export const getPrimaryIndicators = (): IndicatorMetadata[] => {
  return INDICATORS.filter(ind => ind.isPrimary);
};

export const searchIndicators = (query: string): IndicatorMetadata[] => {
  const lowerQuery = query.toLowerCase();
  return INDICATORS.filter(
    ind =>
      ind.name.toLowerCase().includes(lowerQuery) ||
      ind.description.toLowerCase().includes(lowerQuery) ||
      ind.id.toLowerCase().includes(lowerQuery)
  );
};

export const getIndicatorsBySource = (source: DataSource): IndicatorMetadata[] => {
  return INDICATORS.filter(ind => ind.source === source);
};

export const getCategoryInfo = (categoryId: IndicatorCategory) => {
  return CATEGORIES.find(cat => cat.id === categoryId);
};

export const getIndicatorCount = (): number => {
  return INDICATORS.length;
};

export default {
  INDICATORS,
  CATEGORIES,
  getIndicatorById,
  getIndicatorsByCategory,
  getPrimaryIndicators,
  searchIndicators,
  getIndicatorsBySource,
  getCategoryInfo,
  getIndicatorCount,
};
